import { useState } from "react";
import { useDeleteExtra, useUpdateExtra, useSendExtraForm } from "@/hooks/use-project-extras";
import { formatNOK, formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { DBInvoice } from "@/lib/db-types";
import type { useProjectExtras } from "@/hooks/use-project-extras";
import type { BudgetLineRow } from "@/hooks/use-project-detail";
import { Checkbox } from "@/components/ui/checkbox";
import EditableField from "./EditableField";
import ExtraContractCell from "./ExtraContractCell";
import SendFormConfirmDialog from "./SendFormConfirmDialog";
import { SrIcon } from "@/components/ui/SrIcon";
import type { ExtraContract } from "@/hooks/use-extra-contracts";
import { PAYMENT_STATES } from "@/lib/extras-payment";

interface ExtraDetailRowProps {
  ex: ReturnType<typeof useProjectExtras>["data"] extends (infer T)[] ? T : never;
  projectId: string;
  userId: string | null;
  invoices: DBInvoice[];
  budgetLines: BudgetLineRow[];
  selected: boolean;
  onToggleSelect: () => void;
  /** VIB-1281: kontrakt-kolonnen er bak feature_contracts (besluttet i StatisterTab). */
  showContracts: boolean;
  contract: ExtraContract | undefined;
}

export default function ExtraDetailRow({
  ex,
  projectId,
  userId,
  invoices,
  budgetLines,
  selected,
  onToggleSelect,
  showContracts,
  contract,
}: ExtraDetailRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const deleteMut = useDeleteExtra();
  const updateMut = useUpdateExtra();
  const sendFormMut = useSendExtraForm();

  const infoComplete = !!(ex.full_name && ex.address && ex.personal_number && ex.bank_account);
  const linkedBudgetLine = budgetLines.find((bl) => bl.id === ex.budget_line_id) ?? null;
  const formSent = !!ex.extra_form_sent_at;

  const handleSendConfirm = async () => {
    setSendError(null);
    setSendConfirmOpen(false);
    try {
      await sendFormMut.mutateAsync({
        extra_id: ex.id,
        user_id: userId ?? "",
        project_id: projectId,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Sending feilet";
      setSendError(message);
    }
  };

  return (
    <>
      <tr
        className={cn(
          "border-b border-border/50 last:border-0 hover:bg-muted/30 group cursor-pointer",
          expanded && "bg-muted/20"
        )}
        onClick={() => setExpanded(!expanded)}
      >
        <td className="py-2.5 px-2 text-center" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selected}
            onCheckedChange={onToggleSelect}
            disabled={!ex.email}
            aria-label={`Velg ${ex.full_name ?? ex.name ?? "rad"}`}
          />
        </td>
        <td className="py-2.5 px-4">
          <div className="flex items-center gap-1.5">
            <SrIcon name="chevron-right"
              className={cn("w-3 h-3 text-muted-foreground transition-transform", expanded && "rotate-90")}
             />
            <span>{ex.full_name ?? ex.name ?? "—"}</span>
          </div>
        </td>
        <td className="py-2.5 px-4 text-muted-foreground text-xs">{ex.role ?? "—"}</td>
        <td className="py-2.5 px-4 text-muted-foreground text-xs">{ex.phone_number ?? "—"}</td>
        <td className="py-2.5 px-4 text-muted-foreground text-xs">{ex.email ?? "—"}</td>
        <td className="py-2.5 px-4">
          {linkedBudgetLine ? (
            <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary truncate inline-block max-w-[180px]">
              {linkedBudgetLine.description ?? "Uten navn"}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </td>
        <td className="py-2.5 px-4 text-right tabular-nums font-medium">
          {ex.agreed_amount != null ? formatNOK(ex.agreed_amount) : "—"}
        </td>
        <td className="py-2.5 px-4">
          <div className="flex flex-wrap items-center gap-1.5">
            {infoComplete ? (
              <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">Komplett</span>
            ) : (
              <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">Mangler info</span>
            )}
            {formSent && (
              <span
                className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700"
                title={`Skjema sendt ${formatDate(ex.extra_form_sent_at!)}`}
              >
                Sendt {formatDate(ex.extra_form_sent_at!)}
              </span>
            )}
            {sendError && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700" title={sendError}>
                Send feilet
              </span>
            )}
            {/*
              VIB-2092: utbetalingsstatus fra `payment_state`. Erstatter den
              gamle `payment_status`-badgen, som var fri tekst fylt av
              Monday-migreringen og derfor ikke kunne fortelle om noen faktisk
              var meldt klar av en prosjektleder. «Ikke klar» vises ikke —
              det er utgangspunktet for alle, og en badge på hver rad ville
              bare vært støy.
            */}
            {ex.payment_state !== "draft" && (
              <span
                className={cn(
                  "text-xs px-1.5 py-0.5 rounded",
                  PAYMENT_STATES[ex.payment_state].badge,
                )}
              >
                {PAYMENT_STATES[ex.payment_state].label}
              </span>
            )}
          </div>
        </td>
        {showContracts && (
          <td className="py-2.5 px-4">
            <ExtraContractCell
              extraId={ex.id}
              extraName={ex.full_name ?? ex.name ?? "denne personen"}
              projectId={projectId}
              contract={contract}
            />
          </td>
        )}
        <td className="py-2.5 px-4 text-right" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1 justify-end">
            {ex.email && (
              <button
                onClick={() => {
                  setSendError(null);
                  setSendConfirmOpen(true);
                }}
                disabled={sendFormMut.isPending}
                className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 group-focus-within:opacity-100 transition-opacity text-muted-foreground hover:text-primary"
                title={
                  sendError
                    ? "Prøv å sende skjemaet på nytt"
                    : formSent
                    ? "Send skjema på nytt"
                    : "Send skjema"
                }
              >
                {sendFormMut.isPending ? (
                  <SrIcon name="loader2" className="w-3.5 h-3.5 animate-spin"  />
                ) : sendError ? (
                  <SrIcon name="refresh-cw" className="w-3.5 h-3.5"  />
                ) : (
                  <SrIcon name="send" className="w-3.5 h-3.5"  />
                )}
              </button>
            )}
            <button
              onClick={() => deleteMut.mutate({ id: ex.id, project_id: projectId })}
              className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 group-focus-within:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
            >
              <SrIcon name="trash2" className="w-3.5 h-3.5"  />
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-border/50 bg-muted/10">
          <td colSpan={showContracts ? 10 : 9} className="px-4 py-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 ml-5">
              <EditableField
                label="Rolle"
                value={ex.role ?? ""}
                onSave={(v) => updateMut.mutate({ id: ex.id, project_id: projectId, updates: { role: v || null } })}
              />
              <EditableField
                label="Adresse"
                value={ex.address ?? ""}
                onSave={(v) =>
                  updateMut.mutate({ id: ex.id, project_id: projectId, updates: { address: v || null } })
                }
              />
              <EditableField
                label="Personnummer"
                value={ex.personal_number ?? ""}
                mono
                onSave={(v) =>
                  updateMut.mutate({ id: ex.id, project_id: projectId, updates: { personal_number: v || null } })
                }
              />
              <EditableField
                label="Kontonummer"
                value={ex.bank_account ?? ""}
                mono
                onSave={(v) =>
                  updateMut.mutate({ id: ex.id, project_id: projectId, updates: { bank_account: v || null } })
                }
              />
              <EditableField
                label="E-post"
                value={ex.email ?? ""}
                onSave={(v) =>
                  updateMut.mutate({ id: ex.id, project_id: projectId, updates: { email: v || null } })
                }
              />
              <EditableField
                label="Telefon"
                value={ex.phone_number ?? ""}
                onSave={(v) =>
                  updateMut.mutate({
                    id: ex.id,
                    project_id: projectId,
                    updates: { phone_number: v || null },
                  })
                }
              />
              <div>
                <p className="text-[11px] text-muted-foreground mb-0.5">Budsjettlinje</p>
                <select
                  className="w-full h-7 rounded border border-input bg-background px-2 text-xs"
                  value={ex.budget_line_id ?? ""}
                  onChange={(e) =>
                    updateMut.mutate({
                      id: ex.id,
                      project_id: projectId,
                      updates: { budget_line_id: e.target.value || null },
                    })
                  }
                >
                  <option value="">Ingen budsjettlinje</option>
                  {budgetLines.map((bl) => (
                    <option key={bl.id} value={bl.id}>
                      {bl.description ?? "Uten navn"}
                    </option>
                  ))}
                </select>
              </div>
              {invoices.length > 0 && (
                <div>
                  <p className="text-[11px] text-muted-foreground mb-0.5">Koblet til faktura</p>
                  <select
                    className="w-full h-7 rounded border border-input bg-background px-2 text-xs"
                    value={ex.invoice_id ?? ""}
                    onChange={(e) =>
                      updateMut.mutate({
                        id: ex.id,
                        project_id: projectId,
                        updates: { invoice_id: e.target.value || null },
                      })
                    }
                  >
                    <option value="">Ingen</option>
                    {invoices.map((inv) => (
                      <option key={inv.id} value={inv.id}>
                        {inv.description ?? `Faktura ${formatNOK(inv.amount)}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}

      <SendFormConfirmDialog
        open={sendConfirmOpen}
        onOpenChange={setSendConfirmOpen}
        onConfirm={handleSendConfirm}
        recipientName={ex.full_name ?? ex.name ?? "denne personen"}
        recipientEmail={ex.email}
        formSentAt={ex.extra_form_sent_at}
      />
    </>
  );
}
